import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  Box, TextField, IconButton, Tooltip, Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

type Props = {
  result: { client_id: string; client_secret: string } | null;
  clientId: string | null;
  onClose: () => void;
};

export function RotateSecretDialog({ result, clientId, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [result]);

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.client_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <Dialog
      open={result !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ fontFamily: 'Fraunces Variable', fontSize: '1.5rem' }}>
        Rotated secret
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 0 }}>
          The old secret for <strong>{clientId}</strong> is now invalid. All tokens
          issued with it will expire within the hour. Copy the new secret now —
          it cannot be retrieved later.
        </Alert>
        {result && (
          <Box sx={{ position: 'relative' }}>
            <Typography variant="caption" sx={{ color: 'var(--ink-muted)', display: 'block', mb: 0.5 }}>
              New Client Secret
            </Typography>
            <TextField
              value={result.client_secret}
              fullWidth
              multiline
              minRows={1}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'IBM Plex Mono', fontSize: '0.8125rem', pr: 5 },
              }}
              onFocus={(e) => e.target.select()}
            />
            <Tooltip title={copied ? 'Copied' : 'Copy'}>
              <IconButton
                onClick={copy}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 28,
                  right: 6,
                  borderRadius: 0,
                  border: '1px solid var(--rule)',
                  backgroundColor: 'var(--bg)',
                }}
                aria-label={copied ? 'Secret copied' : 'Copy secret'}
              >
                {copied ? <CheckOutlinedIcon fontSize="small" /> : <ContentCopyOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">I've copied it — close</Button>
      </DialogActions>
    </Dialog>
  );
}